import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Shield, Users, FileText, ScrollText, EyeOff, Eye, Sparkles, DollarSign,
  Database, Plus, Save, Trash2, Crown,
} from "lucide-react";
import { toast } from "sonner";

const LVL = { user: 0, moderator: 1, admin: 2, owner: 3 };
const ROLE_COLORS = { owner: "gold-text", admin: "green-text", moderator: "text-[var(--bone)]", user: "text-[var(--bone-faint)]" };

export default function Admin() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/overview");
      setOverview(r.data);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-64 bg-white/[0.04]" /><Skeleton className="h-40 w-full bg-white/[0.04]" /></div>;

  if (denied || !overview) return (
    <div className="litra-card p-10 text-center" data-testid="admin-denied">
      <Shield className="w-8 h-8 mx-auto gold-text" />
      <h2 className="mt-3 font-display text-xl">Staff access only</h2>
      <p className="text-sm text-[var(--bone-muted)] mt-1">You need a moderator, admin, or owner role to view this page.</p>
      <Button variant="outline" className="glass mt-4" onClick={() => navigate("/today")} data-testid="admin-back">Back to app</Button>
    </div>
  );

  const role = overview.my_role;
  const lvl = LVL[role] ?? 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2"><Shield className="w-6 h-6 gold-text" /> Admin Console</h1>
          <p className="text-[var(--bone-muted)] text-sm">Role-based management for Litrametry.</p>
        </div>
        <Badge className={`gold-edge bg-transparent capitalize ${ROLE_COLORS[role]}`} data-testid="admin-my-role">
          {role === "owner" && <Crown className="w-3.5 h-3.5 mr-1.5" />}{role}
        </Badge>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-white/[0.04] flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="moderation" data-testid="admin-tab-moderation">Moderation</TabsTrigger>
          {lvl >= 2 && <TabsTrigger value="content" data-testid="admin-tab-content">Content</TabsTrigger>}
          {lvl >= 2 && <TabsTrigger value="roles" data-testid="admin-tab-roles">Roles</TabsTrigger>}
          {lvl >= 2 && <TabsTrigger value="ai" data-testid="admin-tab-ai">AI Prompts</TabsTrigger>}
          {lvl >= 2 && <TabsTrigger value="pricing" data-testid="admin-tab-pricing">Pricing</TabsTrigger>}
          {lvl >= 2 && <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Log</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="mt-4"><OverviewTab o={overview} /></TabsContent>
        <TabsContent value="moderation" className="mt-4"><ModerationTab /></TabsContent>
        {lvl >= 2 && <TabsContent value="content" className="mt-4"><ContentTab collections={overview.editable_collections} /></TabsContent>}
        {lvl >= 2 && <TabsContent value="roles" className="mt-4"><RolesTab canGrantAdmin={role === "owner"} /></TabsContent>}
        {lvl >= 2 && <TabsContent value="ai" className="mt-4"><AiPromptsTab /></TabsContent>}
        {lvl >= 2 && <TabsContent value="pricing" className="mt-4"><PricingTab /></TabsContent>}
        {lvl >= 2 && <TabsContent value="audit" className="mt-4"><AuditTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, gold }) => (
  <div className="litra-card p-4">
    <div className="flex items-center gap-2 text-[var(--bone-faint)] text-xs"><Icon className="w-3.5 h-3.5" /> {label}</div>
    <div className={`text-2xl font-display mt-1 ${gold ? "gold-text" : "green-text"}`}>{value}</div>
  </div>
);

const OverviewTab = ({ o }) => (
  <div className="space-y-4" data-testid="admin-overview">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Stat label="Users" value={o.counts.users} icon={Users} />
      <Stat label="Decodes" value={o.counts.decodes} icon={FileText} />
      <Stat label="Revenue" value={`$${o.revenue}`} icon={DollarSign} gold />
      <Stat label="Paid" value={o.counts.paid} icon={DollarSign} />
      <Stat label="Lessons" value={o.counts.lessons} icon={Database} />
      <Stat label="Admins" value={o.role_counts.admin} icon={Shield} gold />
      <Stat label="Moderators" value={o.role_counts.moderator} icon={Shield} />
      <Stat label="Mod. pending" value={o.moderation_pending} icon={EyeOff} gold />
    </div>
    <div className="litra-card p-4">
      <div className="text-sm font-display font-semibold mb-3">Recent users</div>
      <div className="space-y-1.5">
        {o.recent_users.map((u) => (
          <div key={u.user_id} className="flex items-center justify-between text-sm px-2.5 py-1.5 rounded-lg bg-white/[0.02]">
            <span className="truncate">{u.username} <span className="text-[var(--bone-faint)]">· {u.email}</span></span>
            <Badge variant="outline" className="text-[10px] capitalize">{u.plan}</Badge>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ModerationTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => { try { const r = await api.get("/admin/moderation"); setData(r.data); } catch (e) { /* */ } }, []);
  useEffect(() => { load(); }, [load]);
  const hide = async (id) => { try { await api.post("/admin/moderation/hide", { report_id: id }); toast.success("Hidden"); load(); } catch (e) { toast.error("Failed"); } };
  const restore = async (id) => { try { await api.post("/admin/moderation/restore", { report_id: id }); toast.success("Restored"); load(); } catch (e) { toast.error("Failed"); } };
  if (!data) return <Skeleton className="h-40 w-full bg-white/[0.04]" />;
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="admin-moderation">
      <div className="litra-card p-4">
        <div className="text-sm font-display font-semibold mb-3 flex items-center gap-2"><Eye className="w-4 h-4 green-text" /> Public decodes</div>
        <div className="space-y-1.5 max-h-[420px] overflow-auto scrollbar-thin">
          {data.public_decodes.length === 0 ? <div className="text-xs text-[var(--bone-faint)]">None public.</div> : data.public_decodes.map((d) => (
            <div key={d.id} className="flex items-center justify-between text-sm px-2.5 py-1.5 rounded-lg bg-white/[0.02]">
              <span className="truncate">{d.input_text} <span className="text-[var(--bone-faint)]">· {d.decode_type}</span></span>
              <Button size="sm" variant="ghost" className="h-7 text-[#C24B4B]" onClick={() => hide(d.id)} data-testid={`mod-hide-${d.id}`}><EyeOff className="w-3.5 h-3.5 mr-1" /> Hide</Button>
            </div>
          ))}
        </div>
      </div>
      <div className="litra-card p-4">
        <div className="text-sm font-display font-semibold mb-3 flex items-center gap-2"><EyeOff className="w-4 h-4 gold-text" /> Hidden</div>
        <div className="space-y-1.5 max-h-[420px] overflow-auto scrollbar-thin">
          {data.hidden.length === 0 ? <div className="text-xs text-[var(--bone-faint)]">Nothing hidden.</div> : data.hidden.map((d) => (
            <div key={d.ref_id} className="flex items-center justify-between text-sm px-2.5 py-1.5 rounded-lg bg-white/[0.02]">
              <span className="truncate">{d.content} <span className="text-[var(--bone-faint)]">· by {d.resolved_by}</span></span>
              <Button size="sm" variant="ghost" className="h-7 green-text" onClick={() => restore(d.ref_id)} data-testid={`mod-restore-${d.ref_id}`}><Eye className="w-3.5 h-3.5 mr-1" /> Restore</Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ContentTab = ({ collections }) => {
  const [active, setActive] = useState(collections[0]);
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async (name) => {
    setItems(null);
    try { const r = await api.get(`/admin/collection/${name}`); setItems(r.data.items); } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(active); }, [active, load]);

  const openEdit = (item) => { setEditing(item); setDraft(JSON.stringify(item || {}, null, 2)); setOpen(true); };
  const save = async () => {
    let parsed;
    try { parsed = JSON.parse(draft); } catch (e) { toast.error("Invalid JSON"); return; }
    try { await api.post(`/admin/collection/${active}`, { item: parsed }); toast.success("Saved"); setOpen(false); load(active); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { try { await api.delete(`/admin/collection/${active}/${id}`); toast.success("Deleted"); load(active); } catch (e) { toast.error("Failed"); } };

  return (
    <div className="space-y-3" data-testid="admin-content">
      <div className="flex flex-wrap gap-1.5">
        {collections.map((c) => (
          <button key={c} onClick={() => setActive(c)} data-testid={`admin-collection-${c}`}
            className={`mono-chip rounded-full px-3 py-1.5 border transition-colors ${active === c ? "border-[rgba(20,90,67,0.6)] bg-[rgba(20,90,67,0.12)] green-text" : "border-white/[0.08] text-[var(--bone-faint)] hover:bg-white/[0.03]"}`}>{c}</button>
        ))}
        <Button size="sm" className="ml-auto bg-[var(--primary)]" onClick={() => openEdit({})} data-testid="admin-content-new"><Plus className="w-3.5 h-3.5 mr-1" /> New</Button>
      </div>
      {!items ? <Skeleton className="h-40 w-full bg-white/[0.04]" /> : (
        <div className="litra-card divide-y divide-white/[0.05]">
          {items.length === 0 ? <div className="p-4 text-xs text-[var(--bone-faint)]">Empty.</div> : items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
              <span className="truncate"><span className="mono-chip text-[var(--bone-faint)]">{it.id}</span> {it.title || it.name || it.label || ""}</span>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="h-7" onClick={() => openEdit(it)} data-testid={`admin-edit-${it.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" className="h-7 text-[#C24B4B]" onClick={() => remove(it.id)} data-testid={`admin-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F1115] border-white/[0.1] max-w-2xl">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><FileText className="w-4 h-4 green-text" /> {editing?.id ? "Edit item" : "New item"} · {active}</DialogTitle></DialogHeader>
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={16} className="font-mono text-xs bg-white/[0.03] border-white/[0.1]" data-testid="admin-content-json" />
          <p className="text-[11px] text-[var(--bone-faint)]">Edit as JSON. Must include an "id" field. Prior version is snapshotted automatically.</p>
          <Button onClick={save} className="bg-[var(--primary)]" data-testid="admin-content-save"><Save className="w-4 h-4 mr-1.5" /> Save</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const RolesTab = ({ canGrantAdmin }) => {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState(null);
  const load = useCallback(async (query = "") => { try { const r = await api.get(`/admin/users?q=${encodeURIComponent(query)}`); setUsers(r.data.users); } catch (e) { /* */ } }, []);
  useEffect(() => { load(); }, [load]);
  const setRole = async (u, role) => {
    try { await api.post("/admin/roles", { user_id: u.user_id, role }); toast.success(`${u.username} → ${role}`); load(q); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const options = canGrantAdmin ? ["user", "moderator", "admin"] : ["user", "moderator"];
  return (
    <div className="space-y-3" data-testid="admin-roles">
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or username" className="bg-white/[0.04] border-white/[0.1]" data-testid="admin-roles-search" onKeyDown={(e) => e.key === "Enter" && load(q)} />
        <Button variant="outline" className="glass" onClick={() => load(q)}>Search</Button>
      </div>
      {!users ? <Skeleton className="h-40 w-full bg-white/[0.04]" /> : (
        <div className="litra-card divide-y divide-white/[0.05]">
          {users.map((u) => (
            <div key={u.user_id} className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm" data-testid={`admin-user-${u.user_id}`}>
              <div className="min-w-0">
                <div className="truncate">{u.username} <span className="text-[var(--bone-faint)]">· {u.email}</span></div>
                <Badge variant="outline" className={`text-[10px] capitalize mt-1 ${ROLE_COLORS[u.role]}`}>{u.role}</Badge>
              </div>
              {u.role === "owner" ? <span className="mono-chip gold-text flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> owner</span> : (
                <div className="flex items-center gap-1 shrink-0">
                  {options.map((opt) => (
                    <Button key={opt} size="sm" variant={u.role === opt ? "default" : "ghost"} disabled={u.role === opt}
                      className={`h-7 px-2 text-xs capitalize ${u.role === opt ? "bg-[rgba(20,90,67,0.3)]" : ""}`}
                      onClick={() => setRole(u, opt)} data-testid={`admin-setrole-${u.user_id}-${opt}`}>{opt}</Button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AiPromptsTab = () => {
  const [prompts, setPrompts] = useState(null);
  const [drafts, setDrafts] = useState({});
  const load = useCallback(async () => {
    try { const r = await api.get("/admin/ai-prompts"); setPrompts(r.data.prompts); setDrafts(Object.fromEntries(r.data.prompts.map((p) => [p.key, p.system_prompt]))); } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (key) => { try { await api.put("/admin/ai-prompts", { key, system_prompt: drafts[key] || "" }); toast.success("Prompt saved"); load(); } catch (e) { toast.error("Failed"); } };
  const reset = async (key) => { try { await api.put("/admin/ai-prompts", { key, system_prompt: "" }); toast.success("Reset to default"); load(); } catch (e) { toast.error("Failed"); } };
  if (!prompts) return <Skeleton className="h-40 w-full bg-white/[0.04]" />;
  return (
    <div className="space-y-4" data-testid="admin-ai">
      {prompts.map((p) => (
        <div key={p.key} className="litra-card p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-sm font-display font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 gold-text" /> {p.label}</div>
            {p.is_overridden ? <Badge className="gold-edge gold-text bg-transparent text-[10px]">Custom</Badge> : <Badge variant="outline" className="text-[10px]">Default</Badge>}
          </div>
          <Textarea value={drafts[p.key] ?? ""} onChange={(e) => setDrafts({ ...drafts, [p.key]: e.target.value })} rows={6}
            placeholder="Leave empty to use the built-in default prompt" className="text-xs bg-white/[0.03] border-white/[0.1]" data-testid={`admin-prompt-${p.key}`} />
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" className="bg-[var(--primary)]" onClick={() => save(p.key)} data-testid={`admin-prompt-save-${p.key}`}><Save className="w-3.5 h-3.5 mr-1.5" /> Save</Button>
            <Button size="sm" variant="ghost" onClick={() => reset(p.key)} data-testid={`admin-prompt-reset-${p.key}`}>Reset to default</Button>
            <details className="ml-auto text-xs text-[var(--bone-faint)]"><summary className="cursor-pointer">View default</summary><pre className="mt-2 whitespace-pre-wrap text-[10px] max-h-40 overflow-auto">{p.default}</pre></details>
          </div>
        </div>
      ))}
    </div>
  );
};

const PricingTab = () => {
  const [rows, setRows] = useState(null);
  const [drafts, setDrafts] = useState({});
  const load = useCallback(async () => {
    try { const r = await api.get("/admin/pricing"); setRows(r.data.plans); setDrafts(Object.fromEntries(r.data.plans.map((p) => [p.plan_id, { name: p.name, price: p.price, tagline: p.tagline }]))); } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (pid) => { try { await api.put("/admin/pricing", { plan_id: pid, ...drafts[pid] }); toast.success("Pricing saved"); load(); } catch (e) { toast.error("Failed"); } };
  const set = (pid, k, v) => setDrafts({ ...drafts, [pid]: { ...drafts[pid], [k]: v } });
  if (!rows) return <Skeleton className="h-40 w-full bg-white/[0.04]" />;
  return (
    <div className="space-y-3" data-testid="admin-pricing">
      {rows.map((p) => (
        <div key={p.plan_id} className="litra-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-display font-semibold capitalize flex items-center gap-2"><DollarSign className="w-4 h-4 green-text" /> {p.plan_id}</div>
            {p.is_overridden && <Badge className="gold-edge gold-text bg-transparent text-[10px]">Overridden</Badge>}
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            <Input value={drafts[p.plan_id]?.name ?? ""} onChange={(e) => set(p.plan_id, "name", e.target.value)} placeholder={p.base_name || "name"} className="bg-white/[0.04] border-white/[0.1]" data-testid={`admin-price-name-${p.plan_id}`} />
            <Input value={drafts[p.plan_id]?.price ?? ""} onChange={(e) => set(p.plan_id, "price", e.target.value)} placeholder={String(p.base_price ?? "price")} className="bg-white/[0.04] border-white/[0.1]" data-testid={`admin-price-price-${p.plan_id}`} />
            <Input value={drafts[p.plan_id]?.tagline ?? ""} onChange={(e) => set(p.plan_id, "tagline", e.target.value)} placeholder="tagline" className="bg-white/[0.04] border-white/[0.1]" data-testid={`admin-price-tagline-${p.plan_id}`} />
          </div>
          <Button size="sm" className="bg-[var(--primary)] mt-2" onClick={() => save(p.plan_id)} data-testid={`admin-price-save-${p.plan_id}`}><Save className="w-3.5 h-3.5 mr-1.5" /> Save</Button>
        </div>
      ))}
    </div>
  );
};

const AuditTab = () => {
  const [logs, setLogs] = useState(null);
  useEffect(() => { (async () => { try { const r = await api.get("/admin/audit"); setLogs(r.data.logs); } catch (e) { /* */ } })(); }, []);
  if (!logs) return <Skeleton className="h-40 w-full bg-white/[0.04]" />;
  return (
    <div className="litra-card divide-y divide-white/[0.05]" data-testid="admin-audit">
      {logs.length === 0 ? <div className="p-4 text-xs text-[var(--bone-faint)]">No audit entries yet.</div> : logs.map((l) => (
        <div key={l.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <ScrollText className="w-3.5 h-3.5 text-[var(--bone-faint)] shrink-0" />
            <span className="mono-chip green-text">{l.action}</span>
            <span className="text-[var(--bone-faint)] truncate">{l.target}</span>
          </div>
          <span className="text-[11px] text-[var(--bone-faint)] shrink-0">{l.actor_email}</span>
        </div>
      ))}
    </div>
  );
};
